import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import { SERVICES } from '@/data/site';
import { SERVICE_AREAS } from '@/data/services';
import { ServiceMark } from '../AmdenMark';
import { Reveal } from '../Reveal';

// Section 2 — four prominent, equally weighted service panels (not small
// generic cards). Division identity (name, accent, route) resolves from the
// shared SERVICES source in data/site.js.
export const ServiceAreas = () => (
  <section id="service-areas" aria-labelledby="service-areas-heading" data-testid="service-areas" className="scroll-mt-28 bg-mist py-16 sm:py-24">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl">
        <p className="flex items-center gap-3">
          <span className="font-mono text-xs font-medium tracking-widest text-gold-700">02</span>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-gold-700">How We Help</span>
        </p>
        <h2 id="service-areas-heading" data-testid="service-areas-heading" className="mt-4 text-balance text-3xl font-extrabold tracking-tight text-navy-900 sm:text-4xl">
          Four service areas. One joined-up approach.
        </h2>
        <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">
          Each service area can support a specific need or work together as part of a wider
          business-improvement programme.
        </p>
      </div>

      <div className="mt-12 grid gap-6 lg:grid-cols-2">
        {SERVICE_AREAS.map((area, i) => {
          const division = SERVICES.find((s) => s.id === area.division);
          return (
            <Reveal key={area.division} delay={(i % 2) * 0.08} className="h-full">
              <article
                id={area.division}
                data-testid={`service-panel-${area.division}`}
                className="group flex h-full scroll-mt-28 flex-col rounded-2xl border border-slate-200 bg-white p-8 transition-[border-color,box-shadow] duration-300 hover:shadow-lift sm:p-10"
                style={{ borderTopWidth: '4px', borderTopColor: division.color }}
              >
                <div className="flex items-center gap-4">
                  <span
                    className="inline-flex h-12 w-12 items-center justify-center rounded-md"
                    style={{ backgroundColor: `${division.color}14` }}
                  >
                    <ServiceMark color={division.color} className="h-7 w-7" />
                  </span>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.16em]" style={{ color: division.color }}>
                      {division.name}
                    </p>
                    <h3 className="mt-1 text-xl font-extrabold tracking-tight text-navy-900 sm:text-2xl">
                      {area.heading}
                    </h3>
                  </div>
                </div>

                <p className="mt-5 text-base leading-relaxed text-slate-600">{area.description}</p>

                <ul className="mt-6 grid gap-x-6 gap-y-2.5 sm:grid-cols-2">
                  {area.services.map((s) => (
                    <li key={s} className="flex items-start gap-2.5 text-sm font-medium text-navy-800">
                      <Check className="mt-0.5 h-4 w-4 shrink-0" style={{ color: division.color }} aria-hidden="true" />
                      {s}
                    </li>
                  ))}
                </ul>

                <div className="mt-auto pt-8">
                  <Link
                    to={division.href}
                    data-testid={`service-panel-${area.division}-link`}
                    className="group/link inline-flex items-center gap-2 rounded-md bg-navy-900 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-navy-700"
                  >
                    Explore {division.name.replace('Amden ', '')}
                    <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover/link:translate-x-0.5" aria-hidden="true" />
                  </Link>
                </div>
              </article>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);
